import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * Find in Store functionality.
 * Allows users to check product availability at physical store locations.
 * Includes GA4 tracking via dataLayer for all user interactions.
 *
 * @extends {DialogComponent}
 */
class FindInStoreComponent extends DialogComponent {
  /** @type {string} */
  #lastSearchedPostcode = '';

  /** @type {boolean} */
  #showSelectSizeLabel = false;

  /** @type {boolean} */
  #userHasSelectedSize = false;

  static #TRIGGER_LABEL_FIND = 'Find in boutique';
  static #TRIGGER_LABEL_SELECT_SIZE = 'Select Size';

  connectedCallback() {
    super.connectedCallback();

    // Initialize dataLayer for GTM tracking
    this.#initializeDataLayer();

    // Listen for variant changes to update the selected variant
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    closestSection?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);

    // Set up event listeners
    this.#setupEventListeners();
    this.#setupSizeSelectionListener();

    // Sync from picker — do not trust server-rendered variant on sized products
    this.#syncVariantFromSelection();

    // Initial state update
    this.#updateButtonState();
  }

  /**
   * Initialize dataLayer for GTM tracking
   */
  #initializeDataLayer() {
    window.dataLayer = window.dataLayer || [];
  }

  /**
   * Get product name from the page
   * @returns {string}
   */
  #getProductName() {
    // Try to get from closest product section
    const productSection = this.closest('.shopify-section, .product, [data-product-id]');

    // Try various selectors for product title
    const titleSelectors = [
      '.product__title',
      '.product-details__title',
      '[data-product-title]',
      'h1',
      '.h1'
    ];

    for (const selector of titleSelectors) {
      const titleElement = productSection?.querySelector(selector);
      if (titleElement?.textContent?.trim()) {
        return titleElement.textContent.trim();
      }
    }

    // Fallback to document title
    return document.title.split(' |')[0].split(' -')[0].trim() || 'Unknown Product';
  }

  /**
   * Get current variant title/name
   * @returns {string}
   */
  #getVariantTitle() {
    // Get current variant ID from URL or selected variant
    let currentVariantId = null;

    // First try URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    currentVariantId = urlParams.get('variant');

    // If not in URL, try to get from selected variant input
    if (!currentVariantId) {
      const variantInput = document.querySelector('input[name="id"]');
      currentVariantId = variantInput?.value;
    }

    // If still no variant ID, try from component data
    if (!currentVariantId) {
      currentVariantId = this.dataset.variantId;
    }


    if (currentVariantId) {
      // Look for the ProductJson script
      const productJsonScript = document.querySelector('script[id*="ProductJson"]');
      if (productJsonScript) {
        try {
          const productData = JSON.parse(productJsonScript.textContent);
          const variant = productData.variants?.find(v => v.id.toString() === currentVariantId.toString());


          if (variant && variant.public_title && variant.public_title !== 'Default Title') {
            return variant.public_title;
          }
        } catch (e) {
        }
      }
    }

    return 'Default';
  }

  /**
   * Push event to dataLayer for GTM tracking
   * @param {string} eventName
   * @param {Object} additionalData
   */
  #trackEvent(eventName, additionalData = {}) {
    const eventData = {
      'event': eventName,
      'event_category': 'Find in Store',
      'product_name': this.#getProductName(),
      'product_variant': this.#getVariantTitle(),
      'product_sku': this.dataset.variantSku || '',
      ...additionalData
    };

    window.dataLayer.push(eventData);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    closestSection?.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
  }

  #getProductSection() {
    return this.closest('.shopify-section, dialog');
  }

  #getVariantPicker() {
    const section = this.#getProductSection();
    if (!section) return null;

    const productId = this.dataset.productId;
    if (productId) {
      return section.querySelector(`variant-picker[data-product-id="${productId}"]`);
    }

    return section.querySelector('variant-picker');
  }

  #requiresSizeSelection() {
    return this.dataset.requiresSizeSelection !== 'false';
  }

  /**
   * Sized products: grey border until the shopper picks a size (ignore Shopify default checked radio).
   * @returns {boolean}
   */
  #hasUserSelectedSize() {
    if (!this.#requiresSizeSelection()) {
      return true;
    }

    return this.#userHasSelectedSize;
  }

  #setupSizeSelectionListener() {
    const picker = this.#getVariantPicker();
    if (!picker || picker.dataset.findInStoreSizeBound === 'true') {
      return;
    }

    picker.dataset.findInStoreSizeBound = 'true';
    picker.addEventListener('change', this.#handleSizePickerChange);
  }

  #handleSizePickerChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') {
      return;
    }

    if (!event.target.closest('.size-grid')) {
      return;
    }

    this.#userHasSelectedSize = true;
    this.#syncVariantFromSelection();
    this.#updateButtonState();
    this.#clearResults();
  };

  #syncVariantFromSelection() {
    if (!this.#hasUserSelectedSize()) {
      this.dataset.variantId = '';
      this.dataset.variantSku = '';
      return;
    }

    const picker = this.#getVariantPicker();
    const checked = picker?.querySelector('fieldset input[type="radio"]:checked');
    const variantIdFromPicker = checked?.dataset.variantId;

    if (variantIdFromPicker) {
      this.dataset.variantId = variantIdFromPicker;
      this.dataset.variantSku = this.#getSkuForVariantId(variantIdFromPicker) || this.dataset.variantSku || '';
      return;
    }

    const section = this.#getProductSection();
    const variantInput = section?.querySelector(
      'product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]'
    );

    if (variantInput?.value) {
      this.dataset.variantId = variantInput.value;
      this.dataset.variantSku = this.#getSkuForVariantId(variantInput.value) || this.dataset.variantSku || '';
    }
  }

  /**
   * @param {string} variantId
   * @returns {string}
   */
  #getSkuForVariantId(variantId) {
    const picker = this.#getVariantPicker();
    if (picker?.dataset.variants) {
      try {
        const variants = JSON.parse(picker.dataset.variants);
        const match = variants.find((/** @type {{ id: number | string, sku?: string }} */ v) =>
          String(v.id) === String(variantId)
        );
        if (match?.sku) {
          return match.sku;
        }
      } catch (e) {
        // ignore JSON parse errors
      }
    }

    const productJsonScript = document.querySelector('script[id*="ProductJson"]');
    if (productJsonScript) {
      try {
        const productData = JSON.parse(productJsonScript.textContent);
        const variant = productData.variants?.find(
          (/** @type {{ id: number | string, sku?: string }} */ v) => String(v.id) === String(variantId)
        );
        return variant?.sku || '';
      } catch (e) {
        // ignore JSON parse errors
      }
    }

    return '';
  }

  #handleVariantSelected = () => {
    this.#syncVariantFromSelection();
    this.#updateButtonState();
  };

  #setupEventListeners() {
    const trigger = this.querySelector('.find-in-store__trigger');
    const closeButton = this.querySelector('.find-in-store__close');
    const searchButton = this.querySelector('.find-in-store__search-button');
    const postcodeInput = this.querySelector('.find-in-store__postcode-input');
    
    trigger?.addEventListener('click', (event) => {
      event.preventDefault();
      this.open();
    });
    
    // Add click handler for close button
    closeButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.close();
    });
    
    searchButton?.addEventListener('click', this.#handleSearch);
    postcodeInput?.addEventListener('keypress', this.#handlePostcodeKeypress);
    postcodeInput?.addEventListener('input', this.#handlePostcodeChange);
  }

  #handleVariantUpdate = (event) => {
    this.#setupSizeSelectionListener();
    this.#syncVariantFromSelection();

    const variant = event.detail?.resource;
    if (variant?.id && this.#hasUserSelectedSize()) {
      this.dataset.variantId = variant.id;
      this.dataset.variantSku = variant.sku || this.#getSkuForVariantId(variant.id) || '';

      // Update product info in modal with new variant
      this.#updateProductInfo(variant);
    } else if (!this.#hasUserSelectedSize()) {
      this.dataset.variantId = '';
      this.dataset.variantSku = '';
    }
    
    // Update button state based on new variant
    this.#updateButtonState();
    
    // Clear any previous search results when variant changes
    this.#clearResults();
  };


  #handleSearch = async (event) => {
    event.preventDefault();
    
    const postcode = this.querySelector('.find-in-store__postcode-input').value.trim();
    
    if (!postcode) {
      this.#showError('Please enter a postcode');
      return;
    }
    
    if (!this.#isValidAustralianPostcode(postcode)) {
      this.#showError('Please enter a valid Australian postcode');
      return;
    }
    
    if (!this.#hasValidVariant()) {
      this.#showError('Please select a size first');
      return;
    }
    
    await this.#searchStores(postcode);
  };
  
  #isValidAustralianPostcode(postcode) {
    // Australian postcodes are 4 digits and follow specific patterns by state/territory
    const postcodePattern = /^[0-9]{4}$/;
    
    if (!postcodePattern.test(postcode)) {
      return false;
    }
    
    // Check first digit for valid Australian state/territory ranges
    const firstDigit = parseInt(postcode.charAt(0));
    
    // Valid first digits: 0-9
    // 0: NT
    // 1: NSW/ACT
    // 2: NSW/ACT
    // 3: VIC
    // 4: QLD
    // 5: SA
    // 6: WA
    // 7: TAS
    // 8-9: Special ranges (PO boxes, etc.)
    return firstDigit >= 0 && firstDigit <= 9;
  };

  #handlePostcodeKeypress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#handleSearch(event);
    }
  };
  
  #handlePostcodeChange = (event) => {
    // Clear results when user starts typing a new postcode
    this.#clearResults();
    
    // Ensure only numbers are entered
    const input = event.target;
    const sanitized = input.value.replace(/[^0-9]/g, '');
    
    // If the value changed after sanitizing, update the input
    if (sanitized !== input.value) {
      input.value = sanitized;
    }
  };

  #hasValidVariant() {
    if (!this.#hasUserSelectedSize()) {
      return false;
    }

    const variantId = this.dataset.variantId;
    const variantSku = this.dataset.variantSku;

    // Require variant ID and SKU for API call
    return Boolean(
      variantId &&
        variantId !== 'null' &&
        variantId !== '' &&
        variantSku &&
        variantSku !== 'null' &&
        variantSku !== ''
    );
  }

  #setTriggerLabel(label) {
    const triggerText = this.querySelector('.find-in-store__trigger-text');
    if (triggerText) {
      triggerText.textContent = label;
    }
  }

  /**
   * @param {boolean} visible
   */
  #setTriggerIconVisible(visible) {
    const triggerIcon = this.querySelector('.find-in-store__trigger-icon');
    if (triggerIcon instanceof HTMLElement) {
      triggerIcon.hidden = !visible;
    }
  }

  #updateButtonState() {
    const trigger = this.querySelector('.find-in-store__trigger');
    if (!trigger) return;

    const hasVariant = this.#hasValidVariant();

    trigger.classList.toggle('find-in-store__trigger--no-size', !this.#hasUserSelectedSize());

    if (hasVariant) {
      this.#showSelectSizeLabel = false;
      this.#setTriggerLabel(FindInStoreComponent.#TRIGGER_LABEL_FIND);
      this.#setTriggerIconVisible(true);
      trigger.setAttribute('aria-label', 'Find this product in store');
    } else if (this.#showSelectSizeLabel) {
      this.#setTriggerLabel(FindInStoreComponent.#TRIGGER_LABEL_SELECT_SIZE);
      this.#setTriggerIconVisible(false);
      trigger.setAttribute('aria-label', 'Select size');
    } else {
      this.#setTriggerLabel(FindInStoreComponent.#TRIGGER_LABEL_FIND);
      this.#setTriggerIconVisible(true);
      trigger.setAttribute('aria-label', 'Find this product in store');
    }
  }

  #clearResults() {
    const resultsContainer = this.querySelector('.find-in-store__results');
    const storesList = this.querySelector('.find-in-store__stores-list');
    
    if (resultsContainer) resultsContainer.style.display = 'none';
    if (storesList) storesList.innerHTML = '';
    
    this.#clearError();
  }

  #updateProductInfo(variantData) {
    const productImage = this.querySelector('.find-in-store__product-image img');
    const productTitle = this.querySelector('.find-in-store__product-title');
    const variantTitle = this.querySelector('.find-in-store__variant-title');
    const productPrice = this.querySelector('.find-in-store__product-price');
    
    // Update image if variant has featured image
    if (productImage && variantData.variant?.featured_image?.src) {
      productImage.src = variantData.variant.featured_image.src.replace(/(\.[^.]*$)/, '_80x80$1');
      productImage.alt = variantData.product?.title || '';
    }
    
    // Update variant title
    if (variantTitle && variantData.variant?.title) {
      if (variantData.variant.title !== 'Default Title') {
        variantTitle.textContent = variantData.variant.title;
        variantTitle.style.display = 'block';
      } else {
        variantTitle.style.display = 'none';
      }
    }
    
    // Update price
    if (productPrice && variantData.variant?.price) {
      // Format price properly
      const price = new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD'
      }).format(variantData.variant.price / 100);
      productPrice.textContent = price;
    }
  }

  #showError(message) {
    const errorContainer = this.querySelector('.find-in-store__error');
    if (errorContainer) {
      errorContainer.textContent = message;
      errorContainer.style.display = 'block';
      setTimeout(() => {
        errorContainer.style.display = 'none';
      }, 5000);
    }
  }

  #showLoading(show = true) {
    const loading = this.querySelector('.find-in-store__loading');
    const results = this.querySelector('.find-in-store__results');
    
    if (loading) loading.style.display = show ? 'block' : 'none';
    if (results) results.style.display = show ? 'none' : 'block';
  }

  async #searchStores(postcode) {
    const variantSku = this.dataset.variantSku;

    if (!variantSku) {
      this.#showError('No variant SKU available');
      return;
    }

    // Store the postcode for CTA tracking
    this.#lastSearchedPostcode = postcode;

    // Track search event
    this.#trackEvent('find_in_store_search', {
      'event_action': 'Search',
      'search_term': postcode
    });

    this.#showLoading(true);
    this.#clearError();
    
    try {
      const response = await fetch(`/a/m/instore/${variantSku}?postCode=${encodeURIComponent(postcode)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const stores = await response.json();
      this.#displayStores(stores);
      
    } catch (error) {
      console.error('Error fetching store data:', error);
      this.#showError('Unable to fetch boutique information. Please try again.');
    } finally {
      this.#showLoading(false);
    }
  }

  #displayStores(stores) {
    const container = this.querySelector('.find-in-store__stores-list');
    const resultsSection = this.querySelector('.find-in-store__results');
    const storeCount = this.querySelector('.find-in-store__store-count');

    if (!container || !resultsSection) return;

    // Show the results section
    resultsSection.style.display = 'block';

    if (!stores || stores.length === 0) {
      // Track no results event
      this.#trackEvent('find_in_store_no_results', {
        'event_action': 'No Results',
        'search_term': this.#lastSearchedPostcode
      });

      container.innerHTML = '<p class="find-in-store__no-results">No stores found for this postcode.</p>';
      return;
    }
    
    // Update store count
    if (storeCount) {
      storeCount.textContent = `${stores.length} STORES`;
    }
    
    container.innerHTML = stores.map((store, index) => this.#renderStoreItem(store, index)).join('');

    // Add event listeners to store CTAs for tracking
    this.#setupStoreEventListeners(stores);
  }

  /**
   * Set up event listeners for store CTA tracking
   * @param {Array} stores - Array of store data
   */
  #setupStoreEventListeners(stores) {
    stores.forEach((store, index) => {
      // Track directions CTA clicks
      const directionsLink = this.querySelector(`.find-in-store__store-item:nth-child(${index + 1}) .find-in-store__view-store`);
      if (directionsLink) {
        directionsLink.addEventListener('click', () => {
          this.#trackEvent('find_in_store_cta', {
            'event_action': 'Directions',
            'store_name': store.store,
            'store_location': this.#extractLocationFromStore(store),
            'search_term': this.#lastSearchedPostcode
          });
        });
      }

      // Note: Email and Phone CTAs would be added here if they exist in the store data
      // Currently only directions CTA is implemented in the API response
    });
  }

  /**
   * Extract location information from store data
   * @param {Object} store - Store data object
   * @returns {string}
   */
  #extractLocationFromStore(store) {
    if (store.address?.city && store.address?.region) {
      return `${store.address.city}, ${store.address.region}`;
    }
    if (store.address?.city) {
      return store.address.city;
    }
    if (store.address?.region) {
      return store.address.region;
    }
    return store.store.replace('CAMILLA AND MARC ', '');
  }

  #renderStoreItem(store, index) {
    const statusClass = this.#getStatusClass(store.text);
    const isInStock = statusClass === 'available';
    const isLowStock = statusClass === 'limited';
    const isOutOfStock = statusClass === 'unavailable';
    
    // Extract address parts for proper formatting
    const address = store.address || {};
    const { street1, street2, city, region, postcode, country } = address;
    const addressLines = [];
    if (street1) addressLines.push(street1);
    if (street2) addressLines.push(street2);
    const cityLine = [city, region].filter(Boolean).join(', ');
    if (cityLine) addressLines.push(cityLine);
    if (country && postcode) addressLines.push(`${country} ${postcode}`);
    else if (country) addressLines.push(country);
    else if (postcode) addressLines.push(postcode);
    
    const addressHtml = addressLines.length
      ? `<div class="find-in-store__store-address">${addressLines.join('<br/>')}</div>`
      : '';

    // Create status with colored dot indicator
    let statusContent = '';
    if (isInStock) {
      statusContent = `<div class="find-in-store__status find-in-store__status--in-stock">IN STOCK</div>`;
    } else if (isLowStock) {
      statusContent = `<div class="find-in-store__status find-in-store__status--low-stock">LOW STOCK</div>`;
    } else {
      statusContent = `<div class="find-in-store__status find-in-store__status--out-of-stock">OUT OF STOCK</div>`;
    }
    
    // Extract location name (remove "CAMILLA AND MARC " prefix)
    const locationName = store.store.replace('CAMILLA AND MARC ', '');
    
    return `
      <div class="find-in-store__store-item">
        <div class="find-in-store__store-main">
          <div class="find-in-store__store-brand">
            <img src="/cdn/shop/files/camilla-marc-cmLogo.svg" alt="CAMILLA AND MARC" class="find-in-store__brand-logo">
          </div>
          <div class="find-in-store__location-name">${locationName}</div>
          ${statusContent}
          ${addressHtml}
          ${store.googleMapsLink ? `<a href="${store.googleMapsLink}" target="_blank" rel="noopener" class="find-in-store__view-store">DIRECTIONS</a>` : ''}
        </div>
      </div>
    `;
  }

  #getStatusClass(statusText) {
    const text = statusText.toLowerCase();
    if (text.includes('in stock')) return 'available';
    if (text.includes('low stock')) return 'limited';
    return 'unavailable';
  }

  #clearError() {
    const errorContainer = this.querySelector('.find-in-store__error');
    if (errorContainer) {
      errorContainer.style.display = 'none';
    }
  }

  open() {
    // Check if a variant is selected before opening
    if (!this.#hasValidVariant()) {
      this.#showSelectSizeLabel = true;
      this.#updateButtonState();
      return;
    }

    // Make sure we have the dialog reference
    if (!this.refs.dialog) {
      console.error('Find in Store: Dialog reference not found');
      return;
    }

    // Track drawer open event
    this.#trackEvent('find_in_store_open', {
      'event_action': 'Open'
    });

    this.showDialog();
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('find-in-store-component')) {
  customElements.define('find-in-store-component', FindInStoreComponent);
}